import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { InnerBlock } from "@/components/InnerBlock";
import { InnerCTA } from "@/components/InnerCTA";
import { BLOG_INTRO, BLOG_POSTS } from "@/data/blog";
import { getBlogImage } from "@/data/blog-images";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch (_) {
    return iso;
  }
}

export default function BlogIndex() {
  return (
    <div data-testid="blog-index">
      <Seo title="Blog" description="Notes on running software businesses in Oman — from our engineers and operators." />
      <PageHero
        eyebrow="Blog"
        h1="Notes from the team"
        intro={BLOG_INTRO}
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Resources", to: "/resources" }, { label: "Blog" }]}
      />
      <InnerBlock tint="white">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {BLOG_POSTS.map((p) => {
            const thumb = getBlogImage(p.slug);
            return (
              <Link
                key={p.slug}
                to={`/resources/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group flex flex-col overflow-hidden rounded-lg border border-wafa-deep/10 bg-white transition-all duration-200 hover:-translate-y-1 hover:border-wafa-deep/25 hover:shadow-[0_18px_40px_-20px_rgba(14,79,58,0.25)]"
              >
                {thumb ? (
                  <picture>
                    <source type="image/webp" srcSet={thumb.webp} />
                    <img
                      src={thumb.jpg}
                      alt={thumb.alt}
                      width={thumb.width}
                      height={thumb.height}
                      loading="lazy"
                      decoding="async"
                      data-testid={`blog-thumb-${p.slug}`}
                      className="block aspect-[1200/630] w-full object-cover"
                    />
                  </picture>
                ) : null}
                <div className="flex flex-1 flex-col p-6">
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-wafa-primary">{p.tag}</div>
                  <div className="mt-3 font-heading text-lg font-semibold text-wafa-deep group-hover:text-wafa-primary">
                    {p.title}
                  </div>
                  <p className="mt-3 text-sm text-wafa-ink/70 line-clamp-4">{p.dek}</p>
                  <div className="mt-6 flex items-center justify-between text-xs text-wafa-ink/60">
                    <span>{formatDate(p.date)}</span>
                    <span>{p.readTime}</span>
                  </div>
                  <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-wafa-deep">
                    Read <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </InnerBlock>
      <InnerCTA heading="Want to hear when we post?" body="Follow us on LinkedIn. We do not run an email list yet — when we do we will tell you what you are signing up to." />
    </div>
  );
}
